import { useIssueFromUser, useMeasuredData } from "@/components/worksheet/apis";
import { OneDayMeasuredData } from "@/components/worksheet/commons/measured_data";
import {
  ImpactScoreResultImg,
  ReportDailyResultImg,
  ThreeLevelScale,
  ThreeLevelScalesType,
} from "@/pages/common/report/WebReport2/commons";
import { OpenInNew } from "@mui/icons-material";
import CheckIcon from "@mui/icons-material/Check";
import {
  Button,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableContainerProps,
  TableHead,
  TableRow,
  Typography,
  TypographyProps,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import SelectedIssueHelpDialog from "./commons/dialogs/SelectedIssueHelpDialog";
import {
  IssueFromUserResponse,
  LifeStyleType,
} from "./commons/issue_from_user";
import MeasuredDataSection from "./commons/MeasuredDataSection";
import { SectionLayout } from "./sleep_summaries_2";

type BasisTableCellType = {
  type: "level" | "check" | "text";
  level?: ThreeLevelScalesType;
  isChecked?: boolean;
  isBad?: boolean;
  text?: string;
};

type ImpactScoreType = {
  name: LifeStyleType;
  caption: string;
  value: number | null;
  eachDayResult: (ThreeLevelScalesType | null)[];
};

/**
 * 気になる課題ページ
 */
export default function IssueFromUser() {
  const { uuid } = useParams<{ uuid: string }>();
  const api = useIssueFromUser();
  const [issueFromUser, setIssueFromUser] = useState<IssueFromUserResponse>();
  const measuredDataApi = useMeasuredData();
  const [allDaysMeasuredData, setAllDaysMeasuredData] = useState<
    OneDayMeasuredData[]
  >([]);

  useEffect(() => {
    if (uuid == null) return;
    (async () => {
      const res = await api.get(uuid);
      setIssueFromUser(res.data);
    })();
    (async () => {
      const res = await measuredDataApi.get(uuid);
      setAllDaysMeasuredData(res.data);
    })();
  }, [uuid, api, measuredDataApi]);

  const measuredData = allDaysMeasuredData.map(
    (oneDay) => oneDay.measured_data
  );

  return (
    <>
      <Stack sx={{ gap: 10 }}>
        {/* 気になっている課題とその判定 */}
        <IssueSection
          uuid={uuid}
          title={issueFromUser?.title}
          judgement={issueFromUser?.judgement}
          additionalJudgement={issueFromUser?.additional_judgement}
          judgementAnnotation={issueFromUser?.judgement_annotation}
          toShowRiskReportLink={issueFromUser?.to_show_risk_report_link}
        />

        {(issueFromUser?.disease_risk_table || issueFromUser?.basis_table) && (
          <>
            {/* 課題に対応する判定指標 */}
            <SectionLayout title="課題に対応する判定指標">
              {issueFromUser?.disease_risk_table && (
                <DiseaseRiskTableSection
                  uuid={uuid}
                  hasSASRisk={issueFromUser.disease_risk_table.has_SAS_risk}
                  hasInsomniaRisk={
                    issueFromUser.disease_risk_table.has_insomnia_risk
                  }
                />
              )}
              {issueFromUser?.basis_table && (
                <BasisSection
                  rows={issueFromUser.basis_table.rows.map((row) => ({
                    itemName: row.item_name,
                    recommendedValue: row.recommended_value,
                    recommendedValueAnnotation:
                      row.recommended_value_annotation,
                    cells: row.cells.map((cell) =>
                      cell != null
                        ? {
                            type: cell?.type,
                            level: cell?.level,
                            isChecked: cell?.is_checked,
                            isBad: cell?.is_bad,
                            text: cell?.text,
                          }
                        : null
                    ),
                  }))}
                  lastRowIsAggregationResult={
                    issueFromUser.basis_table.last_row_is_aggregation_result
                  }
                  medianOfWakeupTime={
                    issueFromUser.basis_table.median_of_wakeup_time
                  }
                />
              )}
            </SectionLayout>

            {/* 悪影響を与えた生活習慣リスト */}
            {issueFromUser?.lifestyle_scores_table && (
              <LifestyleScoresSection
                title={issueFromUser.lifestyle_scores_table.title}
                sleep_factor={issueFromUser.lifestyle_scores_table.sleep_factor}
                impactScores={issueFromUser.lifestyle_scores_table.impact_scores.map(
                  (obj) => ({
                    name: obj.name,
                    caption: obj.caption,
                    value: obj.value,
                    eachDayResult: obj.each_day_result,
                  })
                )}
              />
            )}

            {/* 参考情報 */}
            <SectionLayout title="参考情報">
              {measuredData && (
                <Paper
                  variant="outlined"
                  sx={{ p: "16px 24px", gap: 2, alignItems: "end" }}
                  component={Stack}
                >
                  <MeasuredDataSection
                    measuredData={measuredData}
                    paddingLeft="280px"
                  />
                </Paper>
              )}
            </SectionLayout>
          </>
        )}
      </Stack>
    </>
  );
}

/**
 * 気になっている課題とその判定
 */
function IssueSection({
  uuid,
  title,
  judgement,
  additionalJudgement,
  judgementAnnotation,
  toShowRiskReportLink,
}: {
  uuid?: string;
  title?: string | null;
  judgement?: string | null;
  additionalJudgement?: string | null;
  judgementAnnotation?: string | null;
  toShowRiskReportLink?: boolean | null;
}) {
  return (
    <SectionLayout title="気になっている課題とその判定">
      <WorksheetTableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "506px" }}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography>選択された課題</Typography>
                  <SelectedIssueHelpDialog />
                </Stack>
              </TableCell>
              <TableCell sx={{ width: "506px" }}>
                <Typography>判定</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell
                sx={{
                  borderRight: "1px solid #E0E0E0",
                  verticalAlign: "top",
                }}
              >
                <WorksheetBodyTypography
                  variant="h6"
                  sx={{
                    fontSize: "20px",
                    fontWeight: "500",
                  }}
                >
                  {title ?? "-"}
                </WorksheetBodyTypography>
              </TableCell>
              <TableCell
                sx={{
                  verticalAlign: "top",
                }}
              >
                <Stack direction="column" alignItems="start" gap={4}>
                  <Stack direction="row" alignItems="end" gap={1}>
                    <WorksheetBodyTypography
                      variant="h6"
                      sx={{
                        fontSize: "20px",
                        fontWeight: "500",
                      }}
                    >
                      {judgement ?? "-"}
                    </WorksheetBodyTypography>
                    {additionalJudgement && (
                      <WorksheetBodyTypography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                        }}
                      >
                        {additionalJudgement}
                      </WorksheetBodyTypography>
                    )}
                  </Stack>
                  {judgementAnnotation && (
                    <WorksheetBodyTypography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                      }}
                    >
                      {judgementAnnotation}
                    </WorksheetBodyTypography>
                  )}
                  {toShowRiskReportLink && (
                    <Stack
                      direction="row"
                      justifyContent="end"
                      sx={{ width: "100%" }}
                    >
                      <Button
                        disableElevation
                        variant="text"
                        onClick={() => {
                          if (uuid != null) {
                            window.open(
                              `/facility/report/${uuid}/disease_risk`,
                              "_blank"
                            );
                          }
                        }}
                        endIcon={<OpenInNew />}
                      >
                        疾患リスクレポートを開く
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </WorksheetTableContainer>
    </SectionLayout>
  );
}

function DiseaseRiskTableSection({
  uuid,
  hasSASRisk,
  hasInsomniaRisk,
}: {
  uuid?: string;
  hasSASRisk: boolean | null;
  hasInsomniaRisk: boolean | null;
}) {
  const getRow = (riskName: string) => {
    return (
      <TableRow>
        <TableCell sx={{ borderRight: "1px solid #E0E0E0" }}>
          <WorksheetBodyTypography>{riskName}</WorksheetBodyTypography>
        </TableCell>
        <TableCell>
          <WorksheetBodyTypography
            sx={{ color: "error.main", fontWeight: 500 }}
          >
            リスクあり
          </WorksheetBodyTypography>
        </TableCell>
      </TableRow>
    );
  };
  return (
    <Stack direction="column" gap={4} sx={{ mb: "24px" }}>
      <WorksheetTableContainer sx={{ width: "506.5px" }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "248px" }}>
                <Typography>項目</Typography>
              </TableCell>
              <TableCell>
                <Typography>判定結果</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {hasSASRisk && getRow("睡眠時無呼吸症候群のリスク")}
            {hasInsomniaRisk && getRow("不眠症のリスク")}
          </TableBody>
        </Table>
      </WorksheetTableContainer>
      <Typography
        sx={{
          color: "text.secondary",
          fontSize: "12px",
        }}
      >
        ※詳細は
        <Link
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            cursor: "pointer",
          }}
          onClick={() => {
            uuid &&
              window.open(`/facility/report/${uuid}/disease_risk`, "_blank");
          }}
        >
          疾患リスクレポート
          <OpenInNew sx={{ fontSize: "inherit" }} />
        </Link>
        をご確認ください。
      </Typography>
    </Stack>
  );
}

/**
 * 課題に対応する睡眠指標の記録
 */
function BasisSection({
  rows,
  lastRowIsAggregationResult,
  medianOfWakeupTime,
}: {
  rows: {
    itemName: string;
    recommendedValue: string | null;
    recommendedValueAnnotation: string | null;
    cells: (BasisTableCellType | null)[];
  }[];
  lastRowIsAggregationResult: boolean;
  medianOfWakeupTime: string | null;
}) {
  const hasRecommendedValue = rows.some((row) => row.recommendedValue != null);

  if (rows.length === 0 || rows[0].cells.length === 0) {
    return <></>;
  }

  return (
    <Stack direction="column" gap={2}>
      <Stack direction="column" gap={4}>
        {medianOfWakeupTime && (
          // 起床時刻中央値がある場合はそのためのテーブルを表示
          <WorksheetTableContainer sx={{ width: "525px" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: "280px" }}>
                    <Typography>項目</Typography>
                  </TableCell>
                  <TableCell sx={{ width: "245px" }}>
                    <Typography>測定結果</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ borderRight: "1px solid #E0E0E0" }}>
                    <WorksheetBodyTypography>
                      起床時刻中央値
                    </WorksheetBodyTypography>
                  </TableCell>
                  <TableCell>
                    <WorksheetBodyTypography>
                      {medianOfWakeupTime}
                    </WorksheetBodyTypography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </WorksheetTableContainer>
        )}

        {/* 判定結果の根拠となるテーブルを表示 */}
        <WorksheetTableContainer
          lastRowIsAggregationResult={lastRowIsAggregationResult}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{ width: hasRecommendedValue ? "160px" : "296px" }}
                >
                  <Typography>項目</Typography>
                </TableCell>
                {hasRecommendedValue && (
                  <TableCell sx={{ width: "120px" }}>
                    <Typography>推奨範囲</Typography>
                  </TableCell>
                )}
                {rows[0].cells.map((_, cellIndex) => (
                  <TableCell key={cellIndex} sx={{ width: "104px" }}>
                    <Typography>{cellIndex + 1}日目</Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  <TableCell
                    colSpan={
                      hasRecommendedValue && row.recommendedValue == null
                        ? 2
                        : 1
                    }
                    sx={{
                      borderRight:
                        row.recommendedValue == null
                          ? "1px solid #E0E0E0"
                          : "1px dashed #E0E0E0",
                    }}
                  >
                    <WorksheetBodyTypography>
                      {row.itemName}
                    </WorksheetBodyTypography>
                  </TableCell>
                  {row.recommendedValue != null && (
                    <TableCell
                      sx={{
                        borderRight: "1px solid #E0E0E0",
                      }}
                    >
                      <Stack direction="row" alignItems="flex-start">
                        <WorksheetBodyTypography>
                          {row.recommendedValue}
                        </WorksheetBodyTypography>
                        {row.recommendedValueAnnotation != null && (
                          <RecommendedValueAnnotationSup />
                        )}
                      </Stack>
                    </TableCell>
                  )}
                  {row.cells.map((cell, cellIndex) => (
                    <BasisTableCell key={cellIndex} cell={cell} />
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </WorksheetTableContainer>
      </Stack>
      {rows
        .filter((row) => row.recommendedValueAnnotation != null)
        .map((row, i) => (
          <Typography
            key={i}
            sx={{
              color: "text.secondary",
              fontSize: "12px",
            }}
          >
            {row.recommendedValueAnnotation}
          </Typography>
        ))}
    </Stack>
  );
}

/**
 * 悪影響を与えた生活習慣リスト
 */
function LifestyleScoresSection({
  title,
  sleep_factor,
  impactScores,
}: {
  title: string;
  sleep_factor:
    | "睡眠の質"
    | "睡眠効率"
    | "中途覚醒時間"
    | "入眠潜時"
    | "早すぎる目覚め";
  impactScores: ImpactScoreType[];
}) {
  if (impactScores.length === 0 || impactScores[0].eachDayResult.length === 0) {
    return <></>;
  }

  return (
    <SectionLayout title={title}>
      <Stack direction="column" gap={4}>
        {(impactScores[0].value == null || impactScores[0].value < 55) && (
          <Stack
            sx={{
              backgroundColor: "rgba(255, 58, 48, 0.08)",
              borderRadius: 2,
              p: 5,
            }}
          >
            <Typography sx={{ color: "error.main", fontWeight: 500 }}>
              今回の測定で{sleep_factor}
              に悪影響を強く与えていそうな生活習慣はありませんでした。
            </Typography>
          </Stack>
        )}
        <Stack direction="row" spacing={2} sx={{ alignSelf: "end" }}>
          <ImpactScoreResultImg type={ThreeLevelScale.BAD} />
          <Typography>生活習慣に課題があった日</Typography>
        </Stack>
        <WorksheetTableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "96px" }}>
                  <Typography>影響スコア</Typography>
                </TableCell>
                <TableCell sx={{ width: "184px" }}>
                  <Typography>項目</Typography>
                </TableCell>
                {impactScores[0].eachDayResult.map((_, dayIndex) => (
                  <TableCell key={dayIndex} sx={{ width: "104px" }}>
                    <Typography>{dayIndex + 1}日目</Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {impactScores.map((impactScore, impactScoreIndex) => (
                <TableRow key={impactScoreIndex}>
                  <TableCell
                    align="center"
                    sx={{
                      fontSize: "16px",
                      fontWeight: 500,
                      borderRight: "1px solid #E0E0E0",
                    }}
                  >
                    {impactScore.value ?? "-"}
                  </TableCell>
                  <TableCell sx={{ borderRight: "1px solid #E0E0E0" }}>
                    {impactScore.caption}
                  </TableCell>
                  {impactScore.eachDayResult.map((value, dayIndex) => (
                    <TableCell key={dayIndex}>
                      {value != null && <ImpactScoreResultImg type={value} />}
                      {value == null && (
                        <WorksheetBodyTypography>-</WorksheetBodyTypography>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </WorksheetTableContainer>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", fontSize: "12px" }}
        >
          ※影響スコア：{sleep_factor}
          に悪影響を与えた可能性を評価し、スコアが55以上で悪影響あり、100に近いほど悪影響が強いことを示します。
        </Typography>
      </Stack>
    </SectionLayout>
  );
}

function BasisTableCell({ cell }: { cell: BasisTableCellType | null }) {
  if (cell == null) {
    return (
      <TableCell>
        <WorksheetBodyTypography variant="body2">-</WorksheetBodyTypography>
      </TableCell>
    );
  }

  return (
    <TableCell>
      {cell.type === "level" && cell.level != null && (
        <ReportDailyResultImg type={cell.level} />
      )}
      {cell.type === "check" && (
        <WorksheetBodyTypography>
          {cell.isChecked && (
            <CheckIcon sx={{ color: "#FF3A30", fontSize: "18px" }} />
          )}
          {!cell.isChecked && "-"}
        </WorksheetBodyTypography>
      )}
      {cell.type === "text" && (
        <WorksheetBodyTypography
          sx={{
            color: cell.isBad ? "#FF3A30" : "text.primary",
          }}
        >
          {cell.text}
        </WorksheetBodyTypography>
      )}
    </TableCell>
  );
}

function RecommendedValueAnnotationSup(props: TypographyProps) {
  return (
    <WorksheetBodyTypography
      sx={{
        fontSize: "62.5%",
        position: "relative",
        top: "-2.5px",
      }}
    >
      ※
    </WorksheetBodyTypography>
  );
}

function WorksheetBodyTypography(props: TypographyProps) {
  const { sx, ...rest } = props;
  return (
    <Typography
      sx={{
        color: "text.primary",
        fontSize: "14px",
        fontWeight: 400,
        ...sx,
      }}
      {...rest}
    />
  );
}

function WorksheetTableContainer(
  props: {
    lastRowIsAggregationResult?: boolean;
  } & TableContainerProps
) {
  const { lastRowIsAggregationResult, sx, ...rest } = props;
  return (
    <TableContainer
      sx={{
        ...sx,
        border: "1px solid #E0E0E0",
        "& .MuiTableHead-root": {
          // ヘッダー
          "& .MuiTableRow-root": {
            // ヘッダー中の行
            "& .MuiTableCell-root:not(:first-of-type)": {
              // 各行の2番目以降のセル
              paddingLeft: 0,
              "& .MuiTypography-root": {
                borderLeft: "2px solid rgba(255, 255, 255, 0.12)",
                paddingLeft: 4,
              },
            },
          },
          "& .MuiTableRow-root:last-of-type": {
            // ヘッダー中の最後の行
            "& .MuiTableCell-root": {
              borderBottom: "none",
            },
          },
          "& .MuiTableCell-root": {
            // ヘッダー中のセル
            backgroundColor: "primary.dark",
            color: "white",
            "& .MuiTypography-root": {
              fontSize: "12px",
              fontWeight: "500",
              lineHeight: "12px",
            },
          },
        },
        "& .MuiTableBody-root": {
          // ボディ
          "& .MuiTableRow-root:last-of-type": {
            // ボディ中の最後の行
            "& .MuiTableCell-root": {
              borderBottom: "none",
              borderTop: lastRowIsAggregationResult
                ? "3px double #E0E0E0"
                : "none",
            },
          },
        },
      }}
      {...rest}
    />
  );
}
